import type {
  InterviewGuideDisplay,
  InterviewGuideListItem,
  InterviewGuideQuestion,
  ChecklistItem,
  Competency,
  GuideGenerationStatus,
} from "../types";
import type {
  RawGuideListItem,
  RawGuide,
  RawGuideQuestion,
  RawChecklistItem,
  RawCompetency,
} from "./rawTypes";
import { toUserRef, toScoringGuide } from "./utils";

export function toGuideListItem(raw: RawGuideListItem): InterviewGuideListItem {
  return {
    id: raw.id,
    title: raw.title ?? "",
    status: (raw.status ?? "DRAFT") as InterviewGuideListItem["status"],
    archived: raw.archived ?? false,
    author: toUserRef(raw.author),
    division: raw.division ?? undefined,
    jobTitle: raw.jobTitle ?? undefined,
    questionsCount: raw.questionsCount ?? 0,
    inputRequestedFrom: (raw.inputRequestedFrom ?? []).map(toUserRef),
    inUse: raw.inUse ?? false,
    updatedAt: raw.updatedAt ?? "",
  };
}

export function toGuideDisplay(raw: RawGuide): InterviewGuideDisplay {
  const questions: InterviewGuideQuestion[] = (raw.questions ?? []).map(
    (q: RawGuideQuestion, index: number): InterviewGuideQuestion => ({
      id: q.id,
      text: q.text ?? "",
      scoringGuide: toScoringGuide(q.scoringGuide),
      order: q.order ?? index,
      kssartiTopic: q.kssartiTopic ?? undefined,
      locked: q.locked ?? false,
      savedToBank: q.savedToBank ?? false,
      bankQuestionId: q.bankQuestionId ?? undefined,
      weight: q.weight ?? 0,
    }),
  );

  const checklist: ChecklistItem[] = (raw.checklist ?? []).map(
    (c: RawChecklistItem, index: number): ChecklistItem => ({
      id: c.id,
      text: c.text ?? "",
      order: c.order ?? index,
    }),
  );

  const competencies: Competency[] = (raw.competencies ?? []).map(
    (c: RawCompetency, index: number): Competency => ({
      id: c.id,
      name: c.name ?? "",
      note: c.note ?? undefined,
      order: c.order ?? index,
    }),
  );

  return {
    id: raw.id,
    title: raw.title ?? "",
    status: (raw.status ?? "DRAFT") as InterviewGuideDisplay["status"],
    archived: raw.archived ?? false,
    author: toUserRef(raw.author),
    division: raw.division ?? undefined,
    jobTitle: raw.jobTitle ?? undefined,
    interviewPurpose: raw.interviewPurpose ?? [],
    whenToUse: raw.whenToUse ?? undefined,
    includeChecklist: raw.includeChecklist ?? false,
    includeCompetencies: raw.includeCompetencies ?? false,
    showSuggestions: raw.showSuggestions ?? false,
    responseFormat: raw.responseFormat ?? "NONE",
    questionsCount: raw.questionsCount ?? questions.length,
    inputRequestedFrom: (raw.inputRequestedFrom ?? []).map(toUserRef),
    questions,
    checklist,
    competencies,
    generationStatus: (raw.generationStatus ?? "NONE") as GuideGenerationStatus,
    inUse: raw.inUse ?? false,
    createdAt: raw.createdAt ?? "",
    updatedAt: raw.updatedAt ?? "",
  };
}
